/**
 * Utility functions for the application
 */

/**
 * Retry an operation multiple times with exponential backoff
 * @param operation Function to retry
 * @param maxRetries Maximum number of retries
 * @param initialDelay Initial delay in milliseconds
 * @returns Result of the operation
 */
export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 500
): Promise<T> => {
  let lastError: any;
  let delay = initialDelay;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} failed:`, error);
      lastError = error;
      
      // Wait before next attempt with exponential backoff
      await sleep(delay);
      delay *= 2; // Exponential backoff
    }
  }

  // If we get here, all retries failed
  throw lastError || new Error(`Operation failed after ${maxRetries} attempts`);
};

/**
 * Sleep for a specified number of milliseconds
 * @param ms Milliseconds to sleep
 * @default 0
 * @returns Promise that resolves after the specified time
 */
export const sleep = (ms: number = 0): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Safely access nested object properties
 * @param obj Object to access
 * @param path Dot-separated path to property
 * @returns Property value or undefined
 */
export const safeGet = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Format a date string to a human-readable format
 * @param dateString ISO date string
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Truncate a string to a specified length
 * @param str String to truncate
 * @param maxLength Maximum length
 * @returns Truncated string
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};